import React from 'react';
import { Link } from 'react-router-dom';
import { Reservation } from '../api.reservations';
import BookIcon from './icons/BookIcon';
import PoliceIcon from './icons/PoliceIcon';
import CityTaxIcon from './icons/CityTaxIcon';
import EditIcon from './icons/EditIcon';
import EyeIcon from './icons/EyeIcon';
import KeyIcon from './icons/KeyIcon';
import AppButton from './AppButton';
import { useLang } from '../i18n/LanguageContext';
import { reservationsText } from '../i18n/pages/reservations';
import { colors } from '../theme';
import { getTtlockBadgeInfo, getGuideBadgeInfo } from '../utils/reservationBadges';
import { formatStayRange } from '../utils/stayDates';
import StayHours from './StayHours';
import LuggageStatusIcon from './LuggageStatusIcon';

type StatusBadge = {
  label: string;
  bg: string;
  color: string;
};

type ReservationItemProps = {
  reservation: Reservation;
  layout: 'compact' | 'mobile-card';
  apartmentText: string;
  guestbookStatus: StatusBadge;
  ubyportStatus: StatusBadge;
  /** Null when the apartment doesn't collect city tax — the icon is skipped entirely, same as
   * the optional smart-lock/guide badges. */
  cityTaxStatus?: StatusBadge | null;
  onOpenGuestForm: (reservation: Reservation) => void;
  onEdit: (reservation: Reservation) => void;
};

const INACTIVE_LABEL: Record<string, { text: string; symbol: string; bg: string; color: string; containerBg: string; containerBorder: string; opacity: number; conflict?: string }> = {
  CANCELLED:  { text: 'Cancelled',  symbol: '✕', bg: '#fed7d7', color: '#9b2c2c', containerBg: '#f7f8fa', containerBorder: '#d1d5db', opacity: 0.80 },
  DEPRECATED: { text: 'Deprecated', symbol: '✕', bg: '#e2e8f0', color: '#4a5568', containerBg: '#f7f8fa', containerBorder: '#d1d5db', opacity: 0.85 },
  OVERBOOKED: { text: 'Overbooked', symbol: '⚠', bg: '#fed7d7', color: '#c53030', containerBg: '#fff5f5', containerBorder: '#fed7d7', opacity: 1.00, conflict: 'Inactive – overbooked by a higher-priority calendar' },
};

const ReservationItem: React.FC<ReservationItemProps> = ({
  reservation,
  layout,
  apartmentText,
  guestbookStatus,
  ubyportStatus,
  cityTaxStatus,
  onOpenGuestForm,
  onEdit,
}) => {
  const { lang } = useLang();
  const t = reservationsText[lang];
  const guestsCount = reservation.numberOfGuests ?? '?';

  // Day and month only, unless the stay crosses into the next year — then both ends carry it.
  const stay = formatStayRange(reservation.checkInDate, reservation.checkOutDate);

  const inactiveKey =
    reservation.status === 'OVERBOOKED' ? 'OVERBOOKED'
    : reservation.status === 'CANCELLED' ? 'CANCELLED'
    : reservation.guestbookStatus === 'DEPRECATED' ? 'DEPRECATED'
    : null;
  const inactive = inactiveKey ? INACTIVE_LABEL[inactiveKey] : null;
  const isOverbooked = reservation.status === 'OVERBOOKED';

  // Desktop card's eye icon is always visible (grey = not opened, green = opened) — keep the raw
  // value + fallback tooltip for that. The compact/mobile cluster instead only shows the badge at
  // all once the guide's been opened (see guideBadge below), same "optional" rule as smart-lock.
  const guideOpenedAt = reservation.guideFirstOpenedAt;
  const guideTooltip = guideOpenedAt
    ? `Guide opened: ${new Date(guideOpenedAt).toLocaleString()}`
    : 'Guide not yet opened';
  const guideBadge = getGuideBadgeInfo(reservation);

  const ttlock = getTtlockBadgeInfo(reservation);

  // ── Mobile / compact row ──────────────────────────────────────────────────
  // Eye badge: shown only when guide was opened, sits next to Guestbook + Ubyport badges
  if (layout === 'compact') {
    return (
      <div
        style={{
          border: `1px solid ${inactive ? inactive.containerBorder : '#e2e8f0'}`,
          borderRadius: 8,
          padding: '8px 10px',
          background: inactive ? inactive.containerBg : '#fff',
          boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          opacity: inactive ? inactive.opacity : 1,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {reservation.guestName}
            {reservation.id && <span style={{ fontWeight: 400, color: '#a0aec0', fontSize: '0.72rem', marginLeft: 5 }}>#{reservation.id}</span>}
          </div>
          {inactive?.conflict && (
            <div style={{ fontSize: '0.68rem', color: inactive.color, marginTop: 1 }}>{inactive.conflict}</div>
          )}
          <div style={{ color: '#718096', fontSize: '0.74rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {apartmentText}
          </div>
          <div style={{ color: '#718096', fontSize: '0.68rem', marginTop: 1 }}>
            {stay.from} - {stay.to} | {guestsCount} {t.tableGuests}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <StayHours checkin={reservation.checkinHours} checkout={reservation.checkoutHours} fontSize="0.68rem" />
            <LuggageStatusIcon status={reservation.luggageStatus} />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {inactive && (
              <span title={inactive.text} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: inactive.bg, color: inactive.color, fontWeight: 700, fontSize: '0.7rem' }}>
                {inactive.symbol}
              </span>
            )}
            {/* Guide badge — optional, only when guest has opened the guide */}
            {guideBadge && (
              <span title={guideBadge.tooltip} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: guideBadge.bg }}>
                <EyeIcon size={12} color={guideBadge.color} />
              </span>
            )}
            {/* TTLock PIN badge — optional, only when TTLock is in use */}
            {ttlock && (
              <span title={ttlock.tooltip} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: ttlock.bg }}>
                <KeyIcon size={12} color={ttlock.color} />
              </span>
            )}
            <span title={`Guestbook: ${guestbookStatus.label}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: guestbookStatus.bg }}>
              <BookIcon size={12} color={guestbookStatus.color} />
            </span>
            <span title={`Ubyport: ${ubyportStatus.label}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: ubyportStatus.bg }}>
              <PoliceIcon size={12} color={ubyportStatus.color} />
            </span>
            {cityTaxStatus && (
              <span title={`Místní poplatek: ${cityTaxStatus.label}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: cityTaxStatus.bg }}>
                <CityTaxIcon size={12} color={cityTaxStatus.color} />
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 6, alignItems: 'center' }}>
            {reservation.id && reservation.publicCheckinFormToken && (
              <Link
                to={`/reservations/${reservation.id}/checkin-form`}
                style={{ display: 'inline-flex', alignItems: 'center', height: 34, padding: '0 10px', background: '#edf2f7', color: '#2d3748', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                title="Manage guest slots"
              >
                {t.manage}
              </Link>
            )}
            <AppButton size="standard" variant="blue" onClick={() => onOpenGuestForm(reservation)} title="Guest Form" disabled={isOverbooked}>{isOverbooked ? inactive!.text : t.guestForm}</AppButton>
            <AppButton size="standard" variant="blue" iconOnly onClick={() => onEdit(reservation)} title="Edit">
              <EditIcon size={15} />
            </AppButton>
          </div>
        </div>
      </div>
    );
  }

  // ── Desktop / card view ───────────────────────────────────────────────────
  // Eye icon: always visible next to the guest name, grey = not opened, green = opened
  return (
    <div
      style={{
        border: `1px solid ${inactive ? inactive.containerBorder : '#e2e8f0'}`,
        borderRadius: 10,
        padding: 12,
        background: inactive ? inactive.containerBg : '#fff',
        boxShadow: '0 1px 4px rgba(15, 23, 42, 0.05)',
        position: 'relative',
        opacity: inactive ? inactive.opacity : 1,
      }}
    >
      <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'row', gap: 6, alignItems: 'center' }}>
        {inactive && (
          <span title={inactive.text} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: inactive.bg, color: inactive.color, fontWeight: 700, fontSize: '0.75rem' }}>
            {inactive.symbol}
          </span>
        )}
        {/* TTLock PIN badge */}
        {ttlock && (
          <span title={ttlock.tooltip} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: ttlock.bg }}>
            <KeyIcon size={13} color={ttlock.color} />
          </span>
        )}
        <span title={`Guestbook: ${guestbookStatus.label}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: guestbookStatus.bg, color: guestbookStatus.color }}>
          <BookIcon size={13} color={guestbookStatus.color} />
        </span>
        <span title={`Ubyport: ${ubyportStatus.label}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: ubyportStatus.bg, color: ubyportStatus.color }}>
          <PoliceIcon size={13} color={ubyportStatus.color} />
        </span>
        {cityTaxStatus && (
          <span title={`Místní poplatek: ${cityTaxStatus.label}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: cityTaxStatus.bg, color: cityTaxStatus.color }}>
            <CityTaxIcon size={13} color={cityTaxStatus.color} />
          </span>
        )}
      </div>

      <div style={{ paddingRight: inactive ? 120 : 90 }}>
        <div style={{ fontWeight: 700, marginBottom: 4, color: inactive ? '#6b7280' : undefined, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{reservation.guestName}</span>
          <span title={guideTooltip} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
            <EyeIcon size={13} color={guideOpenedAt ? colors.badgeGreenText : '#cbd5e0'} />
          </span>
        </div>
        {inactive?.conflict && (
          <div style={{ fontSize: '0.8rem', color: inactive.color, marginBottom: 4 }}>{inactive.conflict}</div>
        )}
        <div style={{ color: inactive ? '#9ca3af' : '#4a5568', marginBottom: 4, fontSize: '0.9rem' }}>{apartmentText}</div>
        <div style={{ color: inactive ? '#9ca3af' : '#4a5568', fontSize: '0.85rem' }}>
          {stay.from} → {stay.to}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <StayHours checkin={reservation.checkinHours} checkout={reservation.checkoutHours} fontSize="0.75rem" />
          <LuggageStatusIcon status={reservation.luggageStatus} />
        </div>
        <div style={{ color: inactive ? '#9ca3af' : '#4a5568', fontSize: '0.85rem' }}>{guestsCount} {t.tableGuests}</div>
        {reservation.notes && <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 4 }}>{reservation.notes}</div>}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        {reservation.id && reservation.publicCheckinFormToken && (
          <Link
            to={`/reservations/${reservation.id}/checkin-form`}
            style={{ display: 'inline-flex', alignItems: 'center', height: 34, padding: '0 12px', background: '#edf2f7', color: '#2d3748', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            {t.manage}
          </Link>
        )}
        <AppButton size="standard" variant="blue" onClick={() => onOpenGuestForm(reservation)} disabled={isOverbooked}>
          {isOverbooked ? inactive!.text : t.guestForm}
        </AppButton>
        <AppButton size="standard" variant="blue" iconOnly onClick={() => onEdit(reservation)} title="Edit">
          <EditIcon size={15} />
        </AppButton>
      </div>
    </div>
  );
};

export default ReservationItem;
